import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { blankSpec, createPreset, slugKey, type TAuthPresetSpec } from '../../entities/auth-preset';
import {
  answersFromSpec,
  answersToSpec,
  signsTime,
  skewSeconds,
  type IAnswers,
} from '../preset-create';
import type { IStagedSender } from './use-bind-sender';

const emptyAnswers = (): IAnswers => answersFromSpec(blankSpec());

export interface IHandBuilt {
  answers: IAnswers;
  setAnswers: (next: IAnswers) => void;
  secret: string;
  setSecret: (next: string) => void;
  spec: TAuthPresetSpec;
  reset: () => void;
  ready: boolean;
  save: UseMutationResult<IStagedSender, Error, void>;
}

export const useHandBuilt = (): IHandBuilt => {
  const queryClient = useQueryClient();
  const [answers, setAnswers] = useState<IAnswers>(emptyAnswers);
  const [secret, setSecret] = useState('');

  const spec = useMemo(() => {
    const name = answers.name.trim();
    return answersToSpec({ ...answers, name }, { ...blankSpec(), key: slugKey(name) });
  }, [answers]);

  const save = useMutation<IStagedSender, Error, void>({
    mutationFn: async () => {
      const name = answers.name.trim();
      const version = await createPreset({ key: spec.key, name, spec });
      return {
        presetId: version.presetId,
        versionId: version.id,
        name,
        ...(secret.trim() ? { secret: secret.trim() } : {}),
      };
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['auth-presets'] }),
  });

  const timed = signsTime(answers);
  const ready =
    answers.name.trim().length > 0 &&
    answers.header.trim().length > 0 &&
    (!timed || skewSeconds(answers.skewSec) !== null) &&
    (!timed || answers.timePlace !== 'header' || answers.timeHeader.trim().length > 0);

  const reset = useCallback(() => {
    setAnswers(emptyAnswers());
    setSecret('');
    save.reset();
  }, [save]);

  return { answers, setAnswers, secret, setSecret, spec, reset, ready, save };
};
